import { FormEvent, useMemo, useState } from 'react'
import { Loader2, PlusCircle } from 'lucide-react'
import { useUserTrips } from '../../hooks/useUserTrips'
import { TripEditorCard } from '../../components/user/TripEditorCard'

export default function UserTripsPage() {
  const { trips, loading, error, createTrip, updateTrip, deleteTrip } = useUserTrips({ limit: 50 })

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<'planning' | 'active' | 'completed' | 'archived'>('planning')
  const [privacy, setPrivacy] = useState<'public' | 'unlisted' | 'private'>('private')
  const [type, setType] = useState<'personal' | 'shared' | 'live'>('personal')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [creating, setCreating] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState<string | null>(null)

  const [showCreateForm, setShowCreateForm] = useState(false)

  const sortedTrips = useMemo(() => (
    [...trips].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  ), [trips])

  const handleCreateTrip = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!title.trim()) {
      setFormError('Title is required')
      return
    }

    setCreating(true)
    setFormError(null)
    setFormSuccess(null)

    try {
      await createTrip({
        title: title.trim(),
        description: description.trim() || undefined,
        status,
        privacy,
        type,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      })

      setFormSuccess('Trip created successfully')
      setTitle('')
      setDescription('')
      setStartDate('')
      setEndDate('')
      setShowCreateForm(false)
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create trip'
      setFormError(message)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-8">
      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <PlusCircle className="h-5 w-5" />
              Create a new trip
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Sends a <code>POST /trips</code> request to <code>api.waybook.me</code>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateForm((value) => !value)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            {showCreateForm ? 'Hide form' : 'New trip'}
          </button>
        </div>

        {showCreateForm && (
          <form onSubmit={handleCreateTrip} className="mt-6 grid gap-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-title">
                Title
              </label>
              <input
                id="new-trip-title"
                className="input w-full"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Summer in Italy"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-type">
                Type
              </label>
              <select
                id="new-trip-type"
                className="input w-full"
                value={type}
                onChange={(event) => setType(event.target.value as typeof type)}
              >
                <option value="personal">Personal</option>
                <option value="shared">Shared</option>
                <option value="live">Live</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-description">
              Description
            </label>
            <textarea
              id="new-trip-description"
              className="input w-full min-h-[120px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What is this trip about?"
            />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-status">
                Status
              </label>
              <select
                id="new-trip-status"
                className="input w-full"
                value={status}
                onChange={(event) => setStatus(event.target.value as typeof status)}
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-privacy">
                Privacy
              </label>
              <select
                id="new-trip-privacy"
                className="input w-full"
                value={privacy}
                onChange={(event) => setPrivacy(event.target.value as typeof privacy)}
              >
                <option value="public">Public</option>
                <option value="unlisted">Unlisted</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-start">
                  Start date
                </label>
                <input
                  id="new-trip-start"
                  type="date"
                  className="input w-full"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-trip-end">
                  End date
                </label>
                <input
                  id="new-trip-end"
                  type="date"
                  className="input w-full"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>
          </div>

          {(formError || formSuccess) && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                formError
                  ? 'border-red-100 bg-red-50 text-red-700'
                  : 'border-emerald-100 bg-emerald-50 text-emerald-700'
              }`}
            >
              {formError || formSuccess}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={creating}
              className="btn-primary inline-flex items-center gap-2"
            >
              {creating && <Loader2 className="h-4 w-4 animate-spin" />}
              Create trip
            </button>
          </div>
          </form>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manage existing trips</h2>
            <p className="text-sm text-gray-600">
              Each action issues a <code>PATCH</code> or <code>DELETE</code> request against the live API.
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : sortedTrips.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
              No trips yet. Use the form above to create one.
            </div>
          ) : (
            sortedTrips.map((trip) => (
              <TripEditorCard
                key={trip.id}
                trip={trip}
                onSave={async (id, payload) => {
                  await updateTrip(id, {
                    title: payload.title ?? undefined,
                    description: payload.description ?? undefined,
                    status: payload.status,
                    privacy: payload.privacy,
                    type: payload.type,
                  })
                }}
                onDelete={deleteTrip}
              />
            ))
          )}
        </div>
      </section>
    </div>
  )
}
